// Browser extension communication utilities

const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID;

export interface BrowserBookmarkNode {
  id: string;
  parentId?: string;
  index?: number;
  url?: string;
  title: string;
  dateAdded?: number;
  dateGroupModified?: number;
  children?: BrowserBookmarkNode[];
}

export interface ImportBookmarkItem {
  url: string;
  title: string;
  folderPath: string[];
}

export interface ImportBookmarksData {
  bookmarks: ImportBookmarkItem[];
  createCollections: boolean;
  targetCollectionId?: string;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  failed: number;
  errors: string[];
}

// Response types from extension
interface ExtensionIdResponse {
  extensionId?: string;
  error?: string;
}

interface BookmarksResponse {
  bookmarks?: BrowserBookmarkNode[];
  error?: string;
}

interface ImportResponse {
  success?: boolean;
  imported?: number;
  failed?: number;
  errors?: string[];
  error?: string;
}

// Check if extension is available
export async function isExtensionAvailable(): Promise<boolean> {
  if (!EXTENSION_ID) return false;
  if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) return false;

  try {
    const response = await chrome.runtime.sendMessage(EXTENSION_ID, {
      type: 'GET_EXTENSION_ID',
    }) as ExtensionIdResponse;
    return !!response?.extensionId;
  } catch {
    return false;
  }
}

// Get browser bookmarks tree from extension
export async function getBrowserBookmarks(): Promise<BrowserBookmarkNode[]> {
  if (!EXTENSION_ID) {
    throw new Error('확장 프로그램 ID가 설정되지 않았습니다');
  }

  if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
    throw new Error('Chrome 브라우저에서만 사용 가능합니다');
  }

  try {
    const response = await chrome.runtime.sendMessage(EXTENSION_ID, {
      type: 'GET_BROWSER_BOOKMARKS',
    }) as BookmarksResponse;

    if (response?.error) {
      throw new Error(response.error);
    }

    return response?.bookmarks || [];
  } catch (error) {
    if (error instanceof Error && error.message.includes('Could not establish connection')) {
      throw new Error('Markly 확장 프로그램이 설치되어 있지 않거나 비활성화되어 있습니다');
    }
    throw error;
  }
}

// Import browser bookmarks via extension
export async function importBrowserBookmarks(
  data: ImportBookmarksData
): Promise<ImportResult> {
  if (!EXTENSION_ID) {
    throw new Error('확장 프로그램 ID가 설정되지 않았습니다');
  }

  if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
    throw new Error('Chrome 브라우저에서만 사용 가능합니다');
  }

  try {
    const response = await chrome.runtime.sendMessage(EXTENSION_ID, {
      type: 'IMPORT_BROWSER_BOOKMARKS',
      data,
    }) as ImportResponse;

    if (response?.error) {
      throw new Error(response.error);
    }

    return {
      success: response?.success || false,
      imported: response?.imported || 0,
      failed: response?.failed || 0,
      errors: response?.errors || [],
    };
  } catch (error) {
    if (error instanceof Error && error.message.includes('Could not establish connection')) {
      throw new Error('Markly 확장 프로그램이 설치되어 있지 않거나 비활성화되어 있습니다');
    }
    throw error;
  }
}

// Flatten bookmark tree to array
export function flattenBookmarks(
  nodes: BrowserBookmarkNode[],
  path: string[] = []
): ImportBookmarkItem[] {
  const result: ImportBookmarkItem[] = [];

  for (const node of nodes) {
    if (node.url) {
      result.push({
        url: node.url,
        title: node.title || node.url,
        folderPath: path,
      });
    } else if (node.children) {
      const newPath = node.title ? [...path, node.title] : path;
      result.push(...flattenBookmarks(node.children, newPath));
    }
  }

  return result;
}
